import * as THREE from "three"
import { currentStateSegments } from "../helpers/advance-pool.helper"
import { getEntityMaxWidth, getEntityTotalLength } from "../helpers/entity-geometry.helper"
import { makeAdvanceMaterial } from "../helpers/overlay-materials.helper"
import { paintLane } from "../helpers/lane-painter.helper"

// Dim de los carriles NO-activos: gris plano + opacidad reducida (el color de
// estado se guarda por material en userData.baseColorHex para restaurarlo).
const DIM_ADVANCE_GRAY_HEX = 0xb0b0b0
const DIM_ADVANCE_OPACITY  = 0.5
const FULL_OPACITY         = 0.9

/**
 * Overlay de RENDER de los carriles de avance lineal (progress mode) sobre el
 * visor Three.js. Engine-adjacent (primer plugin de la visión-norte):
 *   - Recibe un GETTER al DxfViewer vivo (sobrevive a recreación del viewer).
 *   - POSEE sus propios THREE.Groups (fuera del componente Vue → sin riesgo de
 *     reactividad Vue2+THREE).
 *   - No lee estado del componente: los datos llegan por `ctx` (inyección de
 *     dependencias). Expone paint / dim / dispose.
 */
export class AdvanceOverlay {
  /** @param {() => (object|null)} getDxf - devuelve el DxfViewer vivo (o null). */
  constructor(getDxf) {
    this._getDxf = getDxf
    this.meshes = Object.create(null)   // handle(string) → THREE.Group
    this._previewGroup = null           // lane transitoria del gesto inline (preview)
  }

  /** Mapa handle→Group (lo usa el raycast del tooltip del componente). */
  getGroups() {
    return this.meshes
  }

  /**
   * Pinta (o repinta) el avance de UNA entidad.
   * @param {string|number} handle
   * @param {object} entity    - entidad del cadJson (vertices, width, layer)
   * @param {string} direction - 'l2r' | 'r2l'
   * @param {Array}  segments  - segmentos de avance del state
   * @param {object} ctx
   * @param {(entity:object)=>Array}   ctx.getStatusesForEntity - statuses de la capa de la entidad
   * @param {(segments:Array)=>Array}  ctx.filterSegmentsForView
   * @param {(item:object)=>number}    ctx.resolveStatusColor   - color hex int
   * @param {?string} ctx.activeLayer  - capa activa (dim del grupo recién pintado)
   * @param {boolean} ctx.highlightOn  - resaltado activo (dim total)
   */
  paintEntity(handle, entity, direction, segments, ctx) {
    const dxf = this._getDxf?.()
    if (!dxf?.HasRenderer?.()) return
    const scene  = dxf.GetScene?.()
    const origin = dxf.GetOrigin?.() || { x: 0, y: 0 }
    if (!scene) return

    this.disposeEntity(handle)

    // Capas CON rechazos (modelo de bolsas): pintamos la DISTRIBUCIÓN vigente del
    // segmento CONCATENADA a lo largo de la polilínea (franja a ancho completo):
    // etapas avanzadas primero, luego los rechazados; lo "por iniciar" queda SIN
    // pintar (se ve la capa base gris). Capas SIN rechazos (zanjas: cavar/tapar/
    // fibra) → carriles PARALELOS acumulativos.
    const statuses = ctx.getStatusesForEntity(entity) || []
    const isRejectLayer = statuses.some(s => Number(s.weight) < 0)

    let items
    if (isRejectLayer) {
      items = currentStateSegments(segments, statuses, getEntityTotalLength(entity))
    } else {
      // Filtro de visualización de estados (por defecto solo el último registrado).
      items = ctx.filterSegmentsForView(segments)
    }
    if (!Array.isArray(items) || items.length === 0) {
      dxf.Render?.()
      return
    }

    // r2l: invertimos para que los avances crezcan desde el extremo opuesto.
    const verts = (direction === 'r2l')
      ? entity.vertices.slice().reverse()
      : entity.vertices

    const polylineWidth = Math.max(getEntityMaxWidth(entity), 0.1)

    const group = new THREE.Group()
    group.name = 'AdvanceOverlay_' + handle
    group.userData.handle = String(handle)
    // Capa (code) a la que pertenece este carril — la usa el dim para atenuar los
    // avances de las capas no-activas cuando hay una capa activa (progress mode).
    group.userData.layerCode = entity?.layer || null
    group.userData.materials = []

    if (isRejectLayer) {
      // CONCATENADO a lo largo del tramo (ancho completo): cada bolsa ocupa su
      // longitud consecutiva desde el extremo de inicio (offset acumulado).
      let offsetMeters = 0
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        const material = makeAdvanceMaterial(ctx.resolveStatusColor(item))
        group.userData.materials.push(material)
        const statusMeta = {
          status:      item.status,
          statusLabel: item.statusLabel,
          statusColor: item.statusColor,
          meters:      item.meters,
        }
        paintLane(group, verts, origin, item.meters, polylineWidth, 0, material, i, statusMeta, offsetMeters)
        offsetMeters += item.meters
      }
    } else {
      // Agrupar por status manteniendo el orden de PRIMERA APARICIÓN — eso
      // estabiliza los carriles entre re-pintados (el carril del primer
      // status no se mueve al agregar nuevos statuses).
      const byStatusOrdered = []
      const statusIndex = new Map()
      for (const seg of items) {
        const key = seg.status
        if (!statusIndex.has(key)) {
          statusIndex.set(key, byStatusOrdered.length)
          byStatusOrdered.push({
            status:      seg.status,
            statusLabel: seg.statusLabel || '',
            statusColor: seg.statusColor || null,
            meters:      0,
          })
        }
        byStatusOrdered[statusIndex.get(key)].meters += seg.meters || 0
      }

      const N = byStatusOrdered.length
      // Si hay N status, cada carril ocupa polylineWidth/N. Reservamos un
      // 10% de ese espacio como gap entre carriles para distinguirlos.
      const laneSlot  = polylineWidth / N
      const laneWidth = laneSlot * 0.9

      for (let i = 0; i < N; i++) {
        const item = byStatusOrdered[i]
        // Offset perpendicular al eje del segmento: distribuimos los N
        // carriles simétricamente alrededor del centro de la polilínea.
        const offsetY = (i - (N - 1) / 2) * laneSlot
        const material = makeAdvanceMaterial(ctx.resolveStatusColor(item))
        group.userData.materials.push(material)
        // Datos del status que se guardan en cada mesh del carril para que
        // al hover el tooltip pueda mostrar info SÓLO de ese status.
        const statusMeta = {
          status:      item.status,
          statusLabel: item.statusLabel,
          statusColor: item.statusColor,
          meters:      item.meters,
        }
        paintLane(group, verts, origin, item.meters, laneWidth, offsetY, material, i, statusMeta)
      }
    }

    scene.add(group)
    this.meshes[handle] = group
    // Los materiales se crean a opacidad plena; si hay una capa activa distinta a
    // la de este grupo, hay que atenuarlo ya para que un repintado no "encienda"
    // de nuevo los avances de las capas no-activas.
    this._dimGroup(group, ctx.activeLayer, ctx.highlightOn)
    dxf.Render?.()
  }

  /**
   * Pinta la lane TRANSITORIA del gesto de avance inline (preview en vivo): el
   * delta que se está marcando, a ancho completo y concatenado desde los metros ya
   * aplicados (startOffsetMeters). No forma parte del avance persistido ni del mapa
   * `meshes`; se repinta en cada frame del arrastre y se limpia al soltar/cancelar.
   * Renderiza POR ENCIMA de los carriles persistidos (statusOrderIndex alto).
   * @param {object} entity
   * @param {string} direction           - 'l2r' | 'r2l'
   * @param {number} startOffsetMeters   - metros ya aplicados (desde donde crece el delta)
   * @param {number} lengthMeters        - metros del delta a pintar
   * @param {number} colorInt            - color hex int del preview
   */
  paintPreview(entity, direction, startOffsetMeters, lengthMeters, colorInt) {
    const dxf = this._getDxf?.()
    if (!dxf?.HasRenderer?.()) return
    const scene  = dxf.GetScene?.()
    const origin = dxf.GetOrigin?.() || { x: 0, y: 0 }
    if (!scene) return

    this.clearPreview()
    if (!(lengthMeters > 1e-6) || !entity?.vertices) { dxf.Render?.(); return }

    const verts = (direction === 'r2l')
      ? entity.vertices.slice().reverse()
      : entity.vertices
    const polylineWidth = Math.max(getEntityMaxWidth(entity), 0.1)

    const group = new THREE.Group()
    group.name = 'AdvanceOverlay_preview'
    const material = makeAdvanceMaterial(colorInt)
    group.userData.materials = [material]
    // statusOrderIndex 0 → z≈0.5 (dentro del frustum, como los carriles persistidos;
    // un índice alto empujaba la z fuera del plano near de la cámara ortográfica y
    // recortaba el quad). Luego subimos el renderOrder para que quede POR ENCIMA
    // (depthTest:false → el orden de dibujo lo manda renderOrder, no la z).
    paintLane(group, verts, origin, lengthMeters, polylineWidth, 0, material, 0, null, Math.max(0, startOffsetMeters || 0))
    group.traverse((obj) => { if (obj.isMesh) obj.renderOrder = 700 })

    scene.add(group)
    this._previewGroup = group
    dxf.Render?.()
  }

  /** Quita y dispone la lane transitoria del preview inline (idempotente). */
  clearPreview() {
    const g = this._previewGroup
    if (!g) return
    const scene = this._getDxf?.()?.GetScene?.()
    if (scene) scene.remove(g)
    g.traverse((obj) => { if (obj.geometry) obj.geometry.dispose?.() })
    for (const m of (g.userData.materials || [])) m?.dispose?.()
    this._previewGroup = null
    this._getDxf?.()?.Render?.()
  }

  /**
   * Muestra/oculta los carriles de UNA capa (por layerCode). Los carriles viven
   * en la escena como grupos propios (no son capas del DxfViewer), así que hay
   * que ligar su visibilidad a la de la capa a mano. Fuerza render.
   */
  setLayerVisible(layerCode, visible) {
    for (const h of Object.keys(this.meshes)) {
      const g = this.meshes[h]
      if (g && g.userData?.layerCode === layerCode) g.visible = !!visible
    }
    this._getDxf?.()?.Render?.()
  }

  /** Atenúa (dim) todos los carriles según capa activa / resaltado. Fuerza render. */
  dim(activeLayer, highlightOn) {
    for (const h of Object.keys(this.meshes)) {
      this._dimGroup(this.meshes[h], activeLayer, highlightOn)
    }
    this._getDxf?.()?.Render?.()
  }

  /**
   * Ajusta los materiales de UN carril: si el grupo es de otra capa (o hay
   * resaltado), gris tenue; si es de la capa activa (o no hay ninguna), restaura
   * su color de estado (userData.baseColorHex) y opacidad plena.
   */
  _dimGroup(group, activeLayer, highlightOn) {
    if (!group) return
    const layerCode = group.userData?.layerCode
    const dim = highlightOn
      ? true
      : !!(activeLayer && layerCode && layerCode !== activeLayer)
    const mats = group.userData?.materials || []
    for (const m of mats) {
      if (!m) continue
      if (dim) {
        m.color.setHex(DIM_ADVANCE_GRAY_HEX)
        m.opacity = DIM_ADVANCE_OPACITY
      } else {
        const base = m.userData && m.userData.baseColorHex
        if (base != null) m.color.setHex(base)
        m.opacity = FULL_OPACITY
      }
    }
  }

  /** Quita y dispone el carril de un handle. */
  disposeEntity(handle) {
    const group = this.meshes[handle]
    if (!group) return
    const dxf   = this._getDxf?.()
    const scene = dxf?.GetScene?.()
    if (scene) scene.remove(group)
    group.traverse((obj) => { if (obj.geometry) obj.geometry.dispose?.() })
    for (const m of (group.userData.materials || [])) m?.dispose?.()
    // Compat: material legacy único.
    group.userData.sharedMaterial?.dispose?.()
    delete this.meshes[handle]
    dxf?.Render?.()
  }

  /** Quita y dispone todos los carriles (incluida la lane transitoria del preview). */
  disposeAll() {
    this.clearPreview()
    for (const h of Object.keys(this.meshes)) this.disposeEntity(h)
  }
}
